import { useUserStore } from "@/store/user";
import type { UserSettings } from "@/interfaces/api";
import useAuth from "./auth";

const makeUserSettingsInstance = (force = false) => {
    const { api } = useNjordaApi();
    const isLoading = ref(false);
    const { loggedIn } = useAuth();
    const { isActiveUser } = storeToRefs(useUserStore());

    const {
        result: userSettings,
        promise,
        pending,
        refetch,
    } = useDataLoaderWithCache<UserSettings>(
        () => api.getAppV1UserSettings(),
        "user-settings",
        {
            forceRefetch: force,
            skip: !loggedIn.value,
        },
    );

    const refetchWhenReady = () => {
        if (loggedIn.value && isActiveUser.value) {
            refetch();
        }
    };

    watch(loggedIn, refetchWhenReady);
    watch(isActiveUser, refetchWhenReady);

    const inProgress = computed(() => {
        return pending.value || isLoading.value;
    });

    const updateSettings = async (settings: UserSettings) => {
        isLoading.value = true;
        await api.patchAppV1UserSettings(settings).catch(() => null);
        await refetch();
        isLoading.value = false;
        return true;
    };

    return {
        userSettings,
        updateSettings,
        promise,
        refetch,
        pending: inProgress,
    };
};

let settingsManager: ReturnType<typeof makeUserSettingsInstance>;

export default function useUserSettings(force = false) {
    if (!settingsManager) {
        settingsManager = makeUserSettingsInstance(force);
    } else if (force) {
        settingsManager.refetch();
    }
    return settingsManager;
}
